import { useRef } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import type { GridCell } from '../../content/site';
import { spring, useMotionEnabled } from '../../lib/motion';
import { useArtReady } from '../../lib/useArtReady';
import { useInViewActive } from '../../lib/useInViewActive';
import { DiamondTravel, type PatrolLeg } from './DiamondTravel';

/**
 * The 6-cell neon frame from the dev frames — used for "Meet Your Heros"
 * (with name labels + traveling diamonds) and the live/post product grid
 * (images only, no diamonds). Layers, back to front: black container art →
 * cell content (photo, or the designed COMING SOON art for null cells) →
 * red grid line-work → DiamondTravel chains on the outer border centerline.
 *
 * Cell rects, corner radii and the border centerline are derived from the
 * designer exports (design-src/Players.svg node 4395:959, Players_mobile.svg
 * node 4395:1140) in each SVG's own viewBox units, so the HTML content layer
 * and the frame art stay aligned at every size. Desktop is the 3×2 frame;
 * below `sm` the 2×3 mobile frame flows the same cells in reading order.
 *
 * The designer's static ornament is a chain of 3 diamonds (60 units wide
 * per diamond, spaced 79 along the path) on each SIDE of the frame — each
 * chain patrols up and down its own side and never rounds a corner
 * (2026-07-16 direction). Both sides run the same profile in phase; the lap
 * direction alternates (right side runs top→bottom, left runs bottom→top),
 * so identical keyframes read as mirrored motion.
 */

/**
 * `corners` = which of this cell's corners the frame art rounds, as a CSS
 * border-radius mask [TL, TR, BR, BL]. The grid line-work only rounds a
 * cell corner where it meets the frame's outer rounded corner or an
 * interior ✕-crossing (the baked diamond-sparkle intersections); corners at
 * T-junctions with the straight border are square. Derived from the cell
 * holes in players-grid(.mobile).svg — content clips to the same shape, so
 * any image dropped into a cell inherits the correct rounding.
 */
type CellRect = {
  l: number;
  t: number;
  w: number;
  h: number;
  corners: [0 | 1, 0 | 1, 0 | 1, 0 | 1];
};

type Variant = {
  boxArt: string;
  gridArt: string;
  /** Mobile grid art ships without the baked glow — CSS supplies it. */
  gridClass?: string;
  aspect: string;
  viewBox: string;
  guide: string;
  /** Side legs of `guide` as lap fractions (corner-tangent boundaries;
   *  re-derive via getTotalLength() on incremental prefixes if the guide
   *  changes). `rest` = the design's static chain spots. */
  legs: PatrolLeg[];
  cells: CellRect[];
  radiusCqw: number;
  labelFontCqw: number;
  diamondSize: number;
  /** Chain spacing along the path, in viewBox units (design: 79 @ 1452). */
  diamondGapUnits: number;
};

const DESKTOP: Variant = {
  boxArt: '/assets/images/frames/players-box.svg',
  gridArt: '/assets/images/frames/players-grid.svg',
  aspect: '1452 / 1137',
  viewBox: '0 0 1452 1137',
  // Outer border centerline: outer edge 94→1358 × 59.43→1076.43, stroke
  // 5.68 → half-inset 2.84; corner r 45.42 − 2.84.
  guide:
    'M 139.42 62.27 H 1312.58 A 42.58 42.58 0 0 1 1355.16 104.85 V 1031.01 ' +
    'A 42.58 42.58 0 0 1 1312.58 1073.59 H 139.42 A 42.58 42.58 0 0 1 96.84 1031.01 ' +
    'V 104.85 A 42.58 42.58 0 0 1 139.42 62.27 Z',
  // Mask rects from Players.svg, as % of 1452×1137. Corner masks [TL,TR,
  // BR,BL]: outer frame corners + the two center ✕-crossings round; border
  // T-junctions stay square.
  cells: [
    { l: 6.773, t: 5.629, w: 28.567, h: 44.085, corners: [1, 0, 1, 0] },
    { l: 35.732, t: 5.629, w: 28.567, h: 44.085, corners: [0, 0, 1, 1] },
    { l: 64.691, t: 5.629, w: 28.567, h: 44.085, corners: [0, 1, 0, 1] },
    { l: 6.773, t: 50.214, w: 28.567, h: 44.085, corners: [0, 1, 0, 1] },
    { l: 35.732, t: 50.214, w: 28.567, h: 44.085, corners: [1, 1, 0, 0] },
    { l: 64.601, t: 50.213, w: 28.567, h: 44.085, corners: [1, 0, 1, 0] },
  ],
  // Guide total length 4466.22.
  legs: [
    { start: 0.27765, end: 0.48502, rest: 0.31 }, // right side (travels down)
    { start: 0.77765, end: 0.98502, rest: 0.81 }, // left side (travels up)
  ],
  radiusCqw: 2.744, // 39.84 units
  labelFontCqw: 1.653, // Druk Wide 24px @1452
  diamondSize: 101.7, // chain diamonds are 60 units wide → 60/0.59 long
  diamondGapUnits: 79,
};

const MOBILE: Variant = {
  boxArt: '/assets/images/frames/players-box-mobile.svg',
  gridArt: '/assets/images/frames/players-grid-mobile.svg',
  gridClass: 'neon-glow-red',
  aspect: '402 / 585',
  viewBox: '0 0 402 585',
  // Outer edge 41→360 × 19.11→564.11, stroke 2.03; corner r 16.28 − 1.02.
  guide:
    'M 57.3 20.12 H 343.7 A 15.26 15.26 0 0 1 358.98 35.38 V 547.83 ' +
    'A 15.26 15.26 0 0 1 343.7 563.09 H 57.3 A 15.26 15.26 0 0 1 42.02 547.83 ' +
    'V 35.38 A 15.26 15.26 0 0 1 57.3 20.12 Z',
  // 2×3 flow — same corner rule as desktop (outer corners + the two
  // center-column ✕-crossings round).
  cells: [
    { l: 10.704, t: 3.614, w: 36.895, h: 30.59, corners: [1, 0, 1, 0] },
    { l: 48.107, t: 3.614, w: 40.94, h: 30.59, corners: [0, 1, 0, 1] },
    { l: 10.704, t: 34.551, w: 36.895, h: 30.59, corners: [0, 1, 1, 0] },
    { l: 48.107, t: 34.551, w: 40.94, h: 30.59, corners: [1, 0, 0, 1] },
    { l: 10.704, t: 65.492, w: 36.895, h: 30.59, corners: [0, 1, 0, 1] },
    { l: 48.107, t: 65.492, w: 40.94, h: 30.59, corners: [1, 0, 1, 0] },
  ],
  // Guide total length 1693.66.
  legs: [
    { start: 0.18327, end: 0.48584, rest: 0.31 }, // right side (travels down)
    { start: 0.68327, end: 0.98584, rest: 0.81 }, // left side (travels up)
  ],
  radiusCqw: 3.532, // 14.2 units
  labelFontCqw: 2.6, // no mobile label spec — extrapolated, pending sign-off
  diamondSize: 28.2, // desktop size × 402/1452
  diamondGapUnits: 21.9, // 79 × 402/1452
};

const COMING_SOON_SRC = '/assets/images/players/coming-soon.webp';

/** Frame art for both variants — the readiness gate for the grid's entrance
 *  choreography and diamond ignition (all four files are ~1–3KB). */
const FRAME_ART = [DESKTOP.boxArt, DESKTOP.gridArt, MOBILE.boxArt, MOBILE.gridArt];

/**
 * Everything a CellGrid draws, for HomePage's ready-gated Reveal: frame art
 * plus the cell photos (and the COMING SOON art when any slot is empty).
 */
export function cellGridArtSrcs(cells: GridCell[]): string[] {
  const photos = cells.filter((c): c is NonNullable<GridCell> => c !== null).map((c) => c.image);
  const comingSoon = cells.some((c) => c === null) ? [COMING_SOON_SRC] : [];
  return [...FRAME_ART, ...new Set(photos), ...comingSoon];
}

/**
 * Entrance choreography (variants inherited from HomePage's Reveal — with
 * reduced motion there is no motion parent, so all of these render static):
 * frame box lands first, cells stagger in, then the neon line-work IGNITES
 * with a flicker, and the diamond sparks fade in last (DiamondTravel owns
 * that delay). All opacity/transform only.
 */
const boxIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.35, ease: 'easeOut' } },
};
const cellIn: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { ...spring.reveal, delay: 0.12 + i * 0.07 },
  }),
};
const gridIgnite: Variants = {
  hidden: { opacity: 0 },
  visible: {
    // Neon-sign flicker-on: two dips before the tube holds steady.
    opacity: [0, 1, 0.35, 1, 0.7, 1],
    transition: { delay: 0.5, duration: 0.9, times: [0, 0.22, 0.38, 0.58, 0.74, 1] },
  },
};

type CellGridProps = {
  cells: GridCell[];
  /** Render the design's white-fade + black name label on filled cells. */
  showNames?: boolean;
  diamonds?: boolean;
  /**
   * Cell photos fetch at high priority — for the products grid, which leads
   * the LIVE/POST band right below the hero. Default (heroes) is low: the
   * grid is below the fold and must not compete with the hero's art.
   */
  priority?: boolean;
};

function CellContent({
  cell,
  variant,
  showNames,
  priority,
}: {
  cell: GridCell;
  variant: Variant;
  showNames: boolean;
  priority: boolean;
}) {
  if (!cell) {
    // The designed COMING SOON cell art (extracted once from Players.svg,
    // reused for every empty slot). The words are baked in the image, so
    // mirror them for screen readers.
    return (
      <>
        <img
          src={COMING_SOON_SRC}
          alt=""
          decoding="async"
          fetchpriority={priority ? 'high' : 'low'}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
        />
        <span className="sr-only">Coming soon</span>
      </>
    );
  }
  return (
    <>
      <img
        src={cell.image}
        alt={showNames ? '' : cell.name}
        decoding="async"
        fetchpriority={priority ? 'high' : 'low'}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
      />
      {showNames && (
        <>
          {/* White fade band behind the name (Players.svg: bottom 43.4% of
              the cell, solid white through ~7%, transparent by ~90%). */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-[43.4%]"
            style={{
              background:
                'linear-gradient(to top, #ffffff 0%, #ffffff 7%, rgba(255, 255, 255, 0) 90%)',
            }}
          />
          <p
            className="absolute inset-x-0 bottom-[8.3%] text-center font-heading uppercase leading-[1.3] tracking-[-0.01em] text-black"
            style={{ fontSize: `${variant.labelFontCqw}cqw` }}
          >
            {cell.name}
          </p>
        </>
      )}
    </>
  );
}

function GridVariant({
  variant,
  cells,
  showNames,
  diamonds,
  active,
  priority,
}: CellGridProps & {
  variant: Variant;
  showNames: boolean;
  diamonds: boolean;
  active: boolean;
  priority: boolean;
}) {
  return (
    <div
      className="relative w-full [container-type:inline-size]"
      style={{ aspectRatio: variant.aspect }}
    >
      <motion.img
        src={variant.boxArt}
        alt=""
        variants={boxIn}
        className="absolute inset-0 h-full w-full"
      />
      <ul className="absolute inset-0 m-0 list-none p-0">
        {variant.cells.map((r, i) => (
          <motion.li
            key={i}
            variants={cellIn}
            custom={i}
            className="group absolute overflow-hidden"
            style={{
              left: `${r.l}%`,
              top: `${r.t}%`,
              width: `${r.w}%`,
              height: `${r.h}%`,
              borderRadius: r.corners.map((on) => (on ? `${variant.radiusCqw}cqw` : '0')).join(' '),
            }}
          >
            <CellContent
              cell={cells[i] ?? null}
              variant={variant}
              showNames={showNames}
              priority={priority}
            />
          </motion.li>
        ))}
      </ul>
      <motion.img
        src={variant.gridArt}
        alt=""
        variants={gridIgnite}
        className={`pointer-events-none absolute inset-0 h-full w-full ${variant.gridClass ?? ''}`}
      />
      {/* Specular gleam sweeping the line-work — masked by the grid art so
          only the neon lines catch the light. Mounted only while the grid
          is on screen (`active`) so off-screen cost is zero. */}
      {active && (
        <div
          aria-hidden="true"
          className="gleam-sweep"
          style={{
            WebkitMaskImage: `url(${variant.gridArt})`,
            maskImage: `url(${variant.gridArt})`,
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
          }}
        />
      )}
      {diamonds && (
        <DiamondTravel
          viewBox={variant.viewBox}
          guidePath={variant.guide}
          legs={variant.legs}
          durationS={14}
          size={variant.diamondSize}
          clusterSize={3}
          clusterGapUnits={variant.diamondGapUnits}
          active={active}
        />
      )}
    </div>
  );
}

export function CellGrid({
  cells,
  showNames = false,
  diamonds = false,
  priority = false,
}: CellGridProps) {
  const ref = useRef<HTMLDivElement>(null);
  const motionOn = useMotionEnabled();
  // Diamonds + gleam hold until the frame art has actually painted — sparks
  // patrolling an invisible frame read as a glitch on cold visits.
  const artReady = useArtReady(FRAME_ART);
  const active = useInViewActive(ref) && motionOn && artReady;

  return (
    <div ref={ref} className="w-full">
      <div className="hidden sm:block">
        <GridVariant
          variant={DESKTOP}
          cells={cells}
          showNames={showNames}
          diamonds={diamonds}
          active={active}
          priority={priority}
        />
      </div>
      <div className="mx-auto max-w-[460px] sm:hidden">
        <GridVariant
          variant={MOBILE}
          cells={cells}
          showNames={showNames}
          diamonds={diamonds}
          active={active}
          priority={priority}
        />
      </div>
    </div>
  );
}
